import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setIsSupported(supported);

    if (supported) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setIsSubscribed(!!sub);
        });
      }).catch(() => {});
    }
  }, []);

  // ── Auto-Subscribe wenn Erlaubnis bereits erteilt wurde ──────────────────
  useEffect(() => {
    if (!isSupported) return;
    if (permission !== 'granted') return;

    const token = localStorage.getItem('token');
    if (!token) return;

    navigator.serviceWorker.ready.then(async (reg) => {
      try {
        let sub = await reg.pushManager.getSubscription();

        // Subscription validieren
        if (sub) {
          const subJson = sub.toJSON();
          if (!subJson.keys || !subJson.keys.p256dh) {
            await sub.unsubscribe().catch(() => {});
            sub = null;
          }
        }

        if (!sub) {
          const keyRes = await axios.get(`${API}/api/push/vapid-public-key`);
          const vapidKey = urlBase64ToUint8Array(keyRes.data.public_key);
          try {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: vapidKey,
            });
          } catch (subscribeErr) {
            // Android-Retry nach kurzer Pause
            await new Promise(r => setTimeout(r, 2000));
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: vapidKey,
            });
          }
        }

        const subJson = sub.toJSON();
        await axios.post(
          `${API}/api/push/subscribe`,
          {
            endpoint: subJson.endpoint,
            keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
            user_agent: navigator.userAgent,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsSubscribed(true);
        localStorage.setItem('push_subscribed', 'true');
      } catch (e) {
        // Silent fail – Nutzer merkt nichts, nächster App-Start versucht es erneut
        console.warn('Auto-Push-Subscribe fehlgeschlagen:', e?.message);
      }
    }).catch(() => {});
  }, [isSupported, permission]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return { success: false, reason: 'not_supported' };
    setLoading(true);
    try {
      const currentPerm = Notification.permission;
      let perm = currentPerm;
      if (currentPerm !== 'granted') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        return { success: false, reason: 'denied' };
      }

      const keyRes = await axios.get(`${API}/api/push/vapid-public-key`);
      const vapidKey = urlBase64ToUint8Array(keyRes.data.public_key);

      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service Worker Timeout')), 15000))
      ]);

      // Bestehende Subscription prüfen und ggf. löschen (Android-Fix: stale subscriptions)
      let subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        // Prüfe ob Endpoint noch erreichbar / kompatibel
        // Bei Fehler: alte löschen und neu subscriben
        try {
          const subJson = subscription.toJSON();
          if (!subJson.keys || !subJson.keys.p256dh) {
            // Inkomplette Subscription → löschen und neu anlegen
            await subscription.unsubscribe();
            subscription = null;
          }
        } catch {
          await subscription.unsubscribe().catch(() => {});
          subscription = null;
        }
      }

      // Neue Subscription anlegen
      if (!subscription) {
        try {
          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey,
          });
        } catch (firstErr) {
          // Android-Fix: Bei "Registration failed" → alle SW-Subscriptions bereinigen und nochmal
          console.warn('Push subscribe fehlgeschlagen (erster Versuch):', firstErr?.message);

          // Alle alten Registrierungen entfernen
          const existingAgain = await reg.pushManager.getSubscription().catch(() => null);
          if (existingAgain) await existingAgain.unsubscribe().catch(() => {});

          // Kurze Pause dann nochmal versuchen
          await new Promise(resolve => setTimeout(resolve, 1500));

          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey,
          });
        }
      }

      const subJson = subscription.toJSON();

      await axios.post(
        `${API}/api/push/subscribe`,
        {
          endpoint: subJson.endpoint,
          keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
          user_agent: navigator.userAgent,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setIsSubscribed(true);
      localStorage.setItem('push_subscribed', 'true');
      return { success: true };
    } catch (err) {
      console.error('Push-Subscription fehlgeschlagen:', err);
      // Hilfreiche Fehlermeldung für häufige Fehler
      let userMsg = err?.message || 'Unbekannter Fehler';
      if (userMsg.includes('Registration failed') || userMsg.includes('push service')) {
        userMsg = 'Push-Dienst nicht erreichbar. Prüfe die Internetverbindung oder starte die App neu.';
      } else if (userMsg.includes('Timeout') || userMsg.includes('timeout')) {
        userMsg = 'Zeitüberschreitung. Bitte erneut versuchen.';
      }
      return { success: false, reason: 'error', error: userMsg };
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(
          `${API}/api/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
      localStorage.removeItem('push_subscribed');
    } catch (err) {
      console.error('Unsubscribe fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { permission, isSupported, isSubscribed, loading, subscribe, unsubscribe };
}

